import { Injectable } from '@angular/core';

export interface AlertButton {
  text: string;
  role?: string;
  handler?: (value?: any) => void;
}

export interface AlertInput {
  name: string;
  type?: string;
  value?: string;
  placeholder?: string;
  attributes?: Record<string, unknown>;
}

export interface AlertOptions {
  header?: string;
  subHeader?: string;
  message?: string;
  buttons?: (string | AlertButton)[];
  inputs?: AlertInput[];
  backdropDismiss?: boolean;
  cssClass?: string;
}

@Injectable({ providedIn: 'root' })
export class AlertService {

  async create(options: AlertOptions): Promise<{ present(): Promise<void>; onDidDismiss(): Promise<{ data?: { values?: Record<string, string> } }> }> {
    let resolvePromise: (value: { data?: { values?: Record<string, string> } }) => void;
    const dismissPromise = new Promise<{ data?: { values?: Record<string, string> } }>((resolve) => {
      resolvePromise = resolve;
    });

    const backdrop = document.createElement('div');
    backdrop.className = 'alert-backdrop';

    const container = document.createElement('div');
    container.className = 'alert-container';
    if (options.cssClass) {
      options.cssClass.split(' ').forEach(c => container.classList.add(c));
    }

    // Header
    if (options.header) {
      const h = document.createElement('div');
      h.className = 'alert-header';
      h.textContent = options.header;
      container.appendChild(h);
    }

    // SubHeader
    if (options.subHeader) {
      const sh = document.createElement('div');
      sh.className = 'alert-subheader';
      sh.textContent = options.subHeader;
      container.appendChild(sh);
    }

    // Message
    if (options.message) {
      const msg = document.createElement('div');
      msg.className = 'alert-message';
      msg.textContent = options.message;
      container.appendChild(msg);
    }

    // Inputs
    const inputEls: HTMLInputElement[] = [];
    if (options.inputs?.length) {
      const inputWrapper = document.createElement('div');
      inputWrapper.className = 'alert-inputs';
      options.inputs.forEach(input => {
        const el = document.createElement('input');
        el.type = input.type || 'text';
        el.name = input.name;
        el.value = input.value || '';
        if (input.placeholder) {
          el.placeholder = input.placeholder;
        }
        el.className = 'alert-input';
        inputEls.push(el);
        inputWrapper.appendChild(el);
      });
      container.appendChild(inputWrapper);
    }

    // Buttons
    const btnWrapper = document.createElement('div');
    btnWrapper.className = 'alert-buttons';
    const buttons = options.buttons || [{ text: 'OK' }];

    const dismiss = (handlerResult?: any) => {
      backdrop.classList.remove('alert-backdrop-visible');
      container.classList.remove('alert-container-visible');
      setTimeout(() => {
        backdrop.remove();
        container.remove();
      }, 200);

      const values: Record<string, string> = {};
      inputEls.forEach(el => { values[el.name] = el.value; });
      resolvePromise({ data: { values } });
    };

    buttons.forEach(btn => {
      const buttonEl = document.createElement('button');
      buttonEl.className = 'alert-btn';

      if (typeof btn === 'string') {
        buttonEl.textContent = btn;
        buttonEl.addEventListener('click', () => dismiss());
      } else {
        buttonEl.textContent = btn.text;
        if (btn.role === 'cancel') {
          buttonEl.classList.add('alert-btn-cancel');
        }
        buttonEl.addEventListener('click', () => {
          const values: Record<string, string> = {};
          inputEls.forEach(el => { values[el.name] = el.value; });
          if (btn.handler) {
            btn.handler(values);
          }
          dismiss();
        });
      }
      btnWrapper.appendChild(buttonEl);
    });
    container.appendChild(btnWrapper);

    if (options.backdropDismiss !== false) {
      backdrop.addEventListener('click', () => dismiss());
    }

    return {
      present: async () => {
        document.body.appendChild(backdrop);
        document.body.appendChild(container);
        requestAnimationFrame(() => {
          backdrop.classList.add('alert-backdrop-visible');
          container.classList.add('alert-container-visible');
        });
        // Auto-focus first input if present
        if (inputEls.length > 0) {
          setTimeout(() => inputEls[0].focus(), 100);
        }
      },
      onDidDismiss: () => dismissPromise,
    };
  }
}
